import React, { useState } from 'react';
import { MapPin, Clock, ExternalLink, Bookmark, Trash2 } from 'lucide-react';

const JobCard = ({ 
  job, 
  isSaved = false, 
  onSaveJob, 
  onRemoveJob, 
  showRemoveButton = false 
}) => {
  const [showAllSkills, setShowAllSkills] = useState(false);

  const handleApplyClick = () => {
    if (job.url && job.url !== '#') {
      window.open(job.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSourceClick = () => {
    if (job.sourceUrl && job.sourceUrl !== '#') {
      window.open(job.sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSaveJob = () => {
    if (onSaveJob) {
      onSaveJob(job);
    }
  };

  const handleRemoveJob = async () => {
    if (onRemoveJob) {
      // Immediately remove from UI (optimistic update)
      const jobId = job.jobId || job.id;
      
      // Call the remove function
      await onRemoveJob(jobId);
    }
  };

  const handleShowAllSkills = () => {
    setShowAllSkills(!showAllSkills);
  };

  return (
    <div className="bg-white border border-gray-400 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Job Header */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{job.title}</h3>
          <p className="text-gray-600 font-medium mb-3">{job.company}</p>
          {/* Job Description */}
          {job.description && (
            <p className="text-gray-700 text-sm leading-relaxed line-clamp-2 mb-3">
              {job.description}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="mb-2 flex gap-2 items-end">
            <span className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
              {job.jobType}
            </span>
            {job.remote !== undefined && (
              <span className="bg-purple-50 border border-purple-200 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                {job.remote ? 'Remote' : 'On-site'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Location and Time */}
      <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          <span>{job.location}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{job.timePosted}</span>
        </div>
      </div>

      {/* Skills Tags */}
      {job.skills && job.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {(showAllSkills ? job.skills : job.skills.slice(0, 5)).map((skill, index) => (
            <span
              key={index}
              className="bg-gray-50 text-gray-500 px-3 py-1 rounded-full border border-gray-400 text-sm font-medium"
            >
              {skill}
            </span>
          ))}
          {job.skills.length > 5 && (
            <button
              onClick={handleShowAllSkills}
              className="bg-gray-50 text-gray-500 px-3 py-1 rounded-full border border-gray-400 text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
            >
              {showAllSkills ? 'Show less' : `+${job.skills.length - 5} more`}
            </button>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button 
          className="border border-gray-400 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex-1 mr-4"
          onClick={handleApplyClick}
          disabled={!job.url || job.url === '#'}
        >
          Apply Now
        </button>
        
        {/* Save/Remove Button */}
        {showRemoveButton ? (
          <button 
            className="bg-red-50 border border-red-400 text-red-700 px-4 py-2 rounded-lg font-semibold hover:bg-red-100 transition-colors duration-200 mr-2 flex items-center gap-2"
            onClick={handleRemoveJob}
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        ) : (
          <button 
            className={`px-4 py-2 rounded-lg font-semibold transition-colors duration-200 mr-2 flex items-center gap-2 ${
              isSaved 
                ? 'bg-green-50 border border-green-400 text-green-700 cursor-not-allowed opacity-75' 
                : 'bg-white border border-gray-400 text-gray-700 hover:bg-gray-50'
            }`}
            onClick={handleSaveJob}
            disabled={isSaved}
          >
            {isSaved ? (
              <>
                <Bookmark className="w-4 h-4" />
                Saved
              </>
            ) : (
              <>
                <Bookmark className="w-4 h-4" />
                Save Job
              </>
            )}
          </button>
        )}
        
        <button 
          className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2"
          onClick={handleSourceClick}
          disabled={!job.sourceUrl || job.sourceUrl === '#'}
        >
          <ExternalLink className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default JobCard;
